import { Platform, Share } from 'react-native'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'

import { API_ENDPOINTS } from '../../../constants/api'
import {
  arrayBufferToBase64,
  buildOrgLogoDownloadFilename,
  parseAttachmentFilenameFromContentDisposition,
} from './downloadOrgLogoAttachment.helper'

export type OrgLogoApiClient = <T = unknown>(
  endpoint: string,
  config?: AxiosRequestConfig,
) => Promise<AxiosResponse<T>>

export {
  parseAttachmentFilenameFromContentDisposition,
  arrayBufferToBase64,
  buildOrgLogoDownloadFilename,
} from './downloadOrgLogoAttachment.helper'

function normalizeResponseBodyToArrayBuffer(data: unknown): ArrayBuffer {
  if (data instanceof ArrayBuffer) {
    return data
  }
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
    const buf = data as Buffer
    return buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength) as ArrayBuffer
  }
  throw new Error('Unexpected response body')
}

/**
 * Downloads an org logo via the authenticated API (same-origin), so the public CDN URL is never
 * opened in the browser. Web: blob + anchor. Native: share sheet with a data URL (no new tab).
 */
export async function downloadOrgLogoViaApi(params: {
  accountId: string
  logoIndex: number
  /** Public CDN URL for this logo — used only to infer file extension for the save dialog (never opened). */
  publicCdnUrl?: string
  apiClient: OrgLogoApiClient
}): Promise<void> {
  const endpoint = API_ENDPOINTS.accounts.logoDownloadAtIndex(params.accountId, params.logoIndex)
  const res = await params.apiClient<ArrayBuffer>(endpoint, {
    method: 'GET',
    responseType: 'arraybuffer',
  })

  if (res.status < 200 || res.status >= 300) {
    throw new Error('Download failed')
  }

  const buffer = normalizeResponseBodyToArrayBuffer(res.data)
  const rawCt = res.headers['content-type']
  const mime =
    typeof rawCt === 'string' && rawCt.length > 0 ? rawCt.split(';')[0].trim() : 'application/octet-stream'
  const rawCd = res.headers['content-disposition']
  const cd = typeof rawCd === 'string' ? rawCd : undefined
  const filenameFromUrl = buildOrgLogoDownloadFilename(params.logoIndex, params.publicCdnUrl)
  const filename = parseAttachmentFilenameFromContentDisposition(cd, filenameFromUrl)

  if (Platform.OS === 'web') {
    if (typeof document === 'undefined' || typeof URL === 'undefined') {
      throw new Error('Download is not available')
    }
    const blob = new Blob([buffer], { type: mime })
    const objectUrl = URL.createObjectURL(blob)
    try {
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = filename
      anchor.rel = 'noopener'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
    return
  }

  const b64 = arrayBufferToBase64(buffer)
  const dataUrl = `data:${mime};base64,${b64}`
  await Share.share({ url: dataUrl })
}
